import { useId, useState } from 'react';
import {
  CURRENCIES,
  currencyDisplay,
  setCurrencyDisplay,
  type CurrencyPreference,
} from './currency-display.js';
import { formatAmount } from './money.js';
import type { Money } from '@intentionalist/shared';

/** Enough to show the effect, and a plausible price rather than a round one. */
const SAMPLE: Money = { minor: 8900, currency: 'USD' };

/**
 * Choosing a default currency (FR-2.18).
 *
 * A row inside Account rather than a destination of its own. A settings menu
 * that grows an entry per preference stops being navigation and becomes a list
 * of every switch in the app, and this is a thing set once -- it does not earn
 * a tab beside Organize and Diagnostics.
 *
 * So it is shaped like the reminder cadence: one labelled `select` and a hint,
 * which is what a quick setting looks like here. `Appearance` keeps its radio
 * buttons because a theme is judged by looking at the whole page while you
 * change it; this is judged by reading one number, and the hint shows it.
 *
 * No re-render is broadcast when the choice changes, and none is needed: every
 * amount in the app is on another destination, so any priced screen a user can
 * see was mounted after they left this one and reads the setting on the way in.
 */
export function CurrencyChoice() {
  const ids = { currency: useId() };
  const [preference, setPreference] = useState<CurrencyPreference>(currencyDisplay);

  return (
    <div className="field">
      <label htmlFor={ids.currency}>Default currency</label>
      <select
        id={ids.currency}
        value={preference}
        onChange={(e) => {
          const next = e.target.value as CurrencyPreference;
          setPreference(next);
          setCurrencyDisplay(next);
        }}
      >
        {CURRENCIES.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/*
        The sample reads from the live setting, so it is the rendering being
        chosen rather than a description of it.
      */}
      <p className="muted hint">
        Prices look like <strong>{formatAmount(SAMPLE)}</strong>.{' '}
        {preference === 'none'
          ? // Said plainly, because "None" is the option most likely to be read
            // as "do not record what prices are in".
            'Amounts are still recorded in US dollars; the code is just not shown.'
          : 'Every amount you enter is assumed to be in this currency.'}
      </p>
    </div>
  );
}
